/**
 * Schema Transformer
 * Transform schemas between formats and for different operating modes
 * Includes visibility rule evaluation and conditional rendering support
 */

import type { FormSchema, FormControl, OperatingMode } from '@atlas-forms/types-js';
import {
  isControlVisible,
  getControlVisibilityState,
  SafeExpressionEvaluator,
  type ExpressionEvaluator,
} from './visibility.resolver';

/**
 * Transform schema for a specific operating mode
 * Creates a copy with visibility settings applied
 * @param schema Original schema
 * @param mode Operating mode
 * @returns Transformed schema for the mode
 */
export function transformSchemaForMode(
  schema: FormSchema,
  mode: OperatingMode
): FormSchema {
  return {
    ...schema,
    controls: (schema.controls || [])
      .map((control: FormControl) => transformControlForMode(control, mode))
      .filter((control: FormControl | null): control is FormControl => control !== null),
  };
}

/**
 * Resolve whether a control should be visible in a given mode
 * @param control Control to check
 * @param mode Operating mode
 * @returns Visibility: 'edit', 'view', or 'none'
 */
export function resolveControlVisibilityForMode(
  control: FormControl,
  mode: OperatingMode
): 'edit' | 'view' | 'none' {
  // Check per-mode visibility settings first
  if (control.modeVisibilitySettings) {
    const modeKey = mode as keyof typeof control.modeVisibilitySettings;
    const modeVisibility = control.modeVisibilitySettings[modeKey];

    if (modeVisibility === false) {
      return 'none';
    }

    if (modeVisibility === true) {
      return mode === 'view' ? 'view' : 'edit';
    }
  }

  // Check base visibility property
  if (control.visibility === 'edit-only' && (mode === 'view' || mode === 'preview')) {
    return 'none';
  }

  if (control.visibility === 'view-only' && mode === 'edit') {
    return 'none';
  }

  if (control.hidden) {
    return 'none';
  }

  // Default: determine based on mode
  if (mode === 'view' || mode === 'preview') {
    return 'view';
  }

  return 'edit';
}

/**
 * Transform a single control for a mode
 * @param control Control to transform
 * @param mode Operating mode
 * @returns Transformed control or null if not visible
 */
function transformControlForMode(
  control: FormControl,
  mode: OperatingMode
): FormControl | null {
  const visibility = resolveControlVisibilityForMode(control, mode);

  if (visibility === 'none') {
    return null;
  }

  // In view/preview mode, disable editing
  if ((mode === 'view' || mode === 'preview') && visibility === 'view') {
    return {
      ...control,
      disabled: true,
      readonly: true,
    };
  }

  // In design mode, add design metadata
  if (mode === 'design') {
    return {
      ...control,
    };
  }

  return control;
}

/**
 * Flatten schema - convert hierarchical structure to flat control list
 * Useful for some rendering scenarios
 * @param schema Schema to flatten
 * @returns Flat schema with single controls array
 */
export function flattenSchema(schema: FormSchema): FormSchema {
  const flatControls: FormControl[] = [];
  let controlOrder = 0;

  // Add controls from sections
  if (schema.sections) {
    schema.sections.forEach((section: any) => {
      const sectionControls = (schema.controls || []).filter((c: FormControl) => c.sectionId === section.id);
      sectionControls.forEach((control: FormControl) => {
        flatControls.push({
          ...control,
          order: controlOrder++,
        });
      });
    });
  }

  // Add controls without section
  (schema.controls || [])
    .filter((c: FormControl) => !c.sectionId)
    .forEach((control: FormControl) => {
      flatControls.push({
        ...control,
        order: controlOrder++,
      });
    });

  return {
    ...schema,
    sections: undefined,
    controls: flatControls,
  };
}

/**
 * Get schema version
 * @param schema Schema to check
 * @returns Version string
 */
export function getSchemaVersion(schema: FormSchema): string {
  return schema.version || '1.0';
}

/**
 * Clone schema deeply
 * @param schema Schema to clone
 * @returns Deep copy of schema
 */
export function cloneSchema(schema: FormSchema): FormSchema {
  return JSON.parse(JSON.stringify(schema));
}

/**
 * Merge schemas - combine multiple schemas into one
 * Used for progressive loading or schema composition
 * @param schemas Schemas to merge
 * @param baseFormId Form ID for merged schema
 * @returns Merged schema
 */
export function mergeSchemas(
  schemas: FormSchema[],
  baseFormId: string
): FormSchema {
  if (schemas.length === 0) {
    throw new Error('Must provide at least one schema to merge');
  }

  const baseSchema = schemas[0];
  let mergedControls = [...(baseSchema.controls || [])];
  let mergedSections = baseSchema.sections ? [...baseSchema.sections] : [];
  let maxControlOrder = Math.max(...mergedControls.map((c) => c.order), -1);
  let maxSectionOrder = Math.max(...(mergedSections?.map((s) => s.order) || []), -1);

  for (let i = 1; i < schemas.length; i++) {
    const schema = schemas[i];

    // Merge sections
    if (schema.sections) {
      schema.sections.forEach((section: any) => {
        mergedSections.push({
          ...section,
          order: ++maxSectionOrder,
        });
      });
    }

    // Merge controls
    (schema.controls || []).forEach((control: FormControl) => {
      mergedControls.push({
        ...control,
        order: ++maxControlOrder,
      });
    });
  }

  return {
    version: baseSchema.version,
    metadata: {
      ...baseSchema.metadata,
      formID: baseFormId,
    },
    sections: mergedSections.length > 0 ? mergedSections : undefined,
    controls: mergedControls,
  };
}

/**
 * Extract controls by section
 * @param schema Schema to extract from
 * @param sectionId Section ID to extract
 * @returns Controls in section
 */
export function getControlsBySection(schema: FormSchema, sectionId: string): FormControl[] {
  return (schema.controls || []).filter((c: FormControl) => c.sectionId === sectionId);
}

/**
 * Extract controls without section
 * @param schema Schema to extract from
 * @returns Top-level controls
 */
export function getTopLevelControls(schema: FormSchema): FormControl[] {
  return (schema.controls || []).filter((c: FormControl) => !c.sectionId);
}

/**
 * Get schema size info (for analytics/monitoring)
 * @param schema Schema to analyze
 */
export function getSchemaStats(schema: FormSchema): {
  totalControls: number;
  totalSections: number;
  controlsByType: Record<string, number>;
} {
  const controlsByType: Record<string, number> = {};

  (schema.controls || []).forEach((control: FormControl) => {
    controlsByType[control.type] = (controlsByType[control.type] || 0) + 1;
  });

  return {
    totalControls: (schema.controls || []).length,
    totalSections: schema.sections?.length || 0,
    controlsByType,
  };
}

/**
 * Filter schema controls based on visibility conditions
 * Evaluates all visibility rules (expression, dependency, mode-based)
 * @param schema Schema to filter
 * @param values Current form values
 * @param mode Operating mode
 * @param evaluator Optional custom expression evaluator (uses SafeExpressionEvaluator by default)
 * @returns Schema with only visible controls
 */
export function filterSchemaByVisibility(
  schema: FormSchema,
  values: Record<string, any>,
  mode: OperatingMode,
  evaluator?: ExpressionEvaluator
): FormSchema {
  const defaultEvaluator = evaluator || new SafeExpressionEvaluator();

  const visibleControls = (schema.controls || []).filter((control: FormControl) =>
    isControlVisible(control, values, mode, defaultEvaluator)
  );

  return {
    ...schema,
    controls: visibleControls,
    // Also filter sections if they exist
    sections: schema.sections?.filter((section: any) => {
      // Keep section if it has any visible controls
      return visibleControls.some((c: FormControl) => c.sectionId === section.id);
    }),
  };
}

/**
 * Get visibility states for all controls
 * Useful for debugging and UI updates
 * @param schema Schema to analyze
 * @param values Current form values
 * @param mode Operating mode
 * @param evaluator Optional custom expression evaluator
 * @returns Map of control ID to visibility state
 */
export function getControlVisibilityStates(
  schema: FormSchema,
  values: Record<string, any>,
  mode: OperatingMode,
  evaluator?: ExpressionEvaluator
): Map<string, 'visible' | 'hidden' | 'readonly'> {
  const defaultEvaluator = evaluator || new SafeExpressionEvaluator();
  const states = new Map<string, 'visible' | 'hidden' | 'readonly'>();

  (schema.controls || []).forEach((control: FormControl) => {
    const state = getControlVisibilityState(control, values, mode, defaultEvaluator);
    states.set(control.id, state);
  });

  return states;
}

/**
 * Get all controls affected by a specific field change
 * Useful for form update optimization
 * @param schema Schema to analyze
 * @param fieldId Field that changed
 * @returns Array of control IDs affected by this field
 */
export function getAffectedControlIds(schema: FormSchema, fieldId: string): string[] {
  return (schema.controls || [])
    .filter((control: FormControl) => {
      if (control.visibilityRule?.type === 'dependency' && control.visibilityRule.dependsOn) {
        return control.visibilityRule.dependsOn.includes(fieldId);
      }
      return false;
    })
    .map((c: FormControl) => c.id);
}
